import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Chip,
  Divider,
  Avatar,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { ArrowBack, ContactMail, Schedule, Edit, OutlinedFlag } from '@mui/icons-material';
import ReportProfileDialog from '../components/ReportProfileDialog';
import { useApp } from '../context/AppContext';
import {
  caretakerProfileService,
  isCaretakerProfileUnavailable,
} from '../services/caretakerProfileService';
import { caregiverProfileService } from '../services/caregiverProfileService';
import { CaretakerProfile } from '../services/caretakerProfileService';
import NotAFitMenuItem from '../components/NotAFitMenuItem';
import { IconButton, Menu } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CaretakerProfileHeader from '../components/CaretakerProfileHeader';
import CaregiverBadgeRow from '../components/CaregiverBadgeRow';
import FavoriteButton from '../components/FavoriteButton';
import ProfileCompletionMeter from '../components/ProfileCompletionMeter';
import StateAgencyVerifyLink from '../components/StateAgencyVerifyLink';
import CaretakerBio from '../components/CaretakerBio';
import ProfileSummary from '../components/caregiver/ProfileSummary';
import CaretakerServices from '../components/CaretakerServices';
import CaretakerAvailability from '../components/CaretakerAvailability';
import CaretakerReviews from '../components/CaretakerReviews';
import CaretakerCertifications from '../components/CaretakerCertifications';
import ContactCaretakerButton from '../components/ContactCaretakerButton';
import BookCaretakerButton from '../components/BookCaretakerButton';
import BioManagementSidebar from '../components/sidebars/BioManagementSidebar';
import ServicesManagementSidebar from '../components/sidebars/ServicesManagementSidebar';
import AvailabilityManagementSidebar from '../components/sidebars/AvailabilityManagementSidebar';
import CertificationsManagementSidebar from '../components/sidebars/CertificationsManagementSidebar';

const CaretakerProfilePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { state } = useApp();
  const [caretaker, setCaretaker] = useState<CaretakerProfile | any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSidebar, setActiveSidebar] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState<{ message: string } | null>(null);
  const [overflowAnchor, setOverflowAnchor] = useState<null | HTMLElement>(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);

  // Check if the current user is viewing their own profile
  const isOwner = state.user?.id === id && state.user?.role === 'CAREGIVER';

  useEffect(() => {
    const fetchCaretakerProfile = async () => {
      if (!id) {
        setError('Caretaker ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // If viewing own profile, use the management service for full data
        // Otherwise, use the public profile service
        let profileData;
        if (isOwner) {
          const caregiverProfile = await caregiverProfileService.getProfile();
          // Transform CaregiverProfile to CaretakerProfile interface
          profileData = {
            ...caregiverProfile,
            // Add missing fields that CaretakerProfile expects
            reviews: [],
            averageRating: 0,
            reviewCount: 0,
            ratingBreakdown: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
            // Add computed fields that CaretakerProfile expects
            displayName: `${caregiverProfile.firstName} ${caregiverProfile.lastName}`,
            experienceText: caregiverProfile.yearsExperience ? `${caregiverProfile.yearsExperience} years` : 'Not specified',
            hourlyRateText: caregiverProfile.hourlyRate ? `$${caregiverProfile.hourlyRate}/hour` : 'Not specified',
            languagesText: caregiverProfile.languages.length > 0 ? caregiverProfile.languages.join(', ') : 'Not specified',
          };
        } else {
          profileData = await caretakerProfileService.getCaretakerProfile(id);
          // FEAT-042 — neutral profile-not-available state when viewer or
          // target has marked Not a Fit on the pair. No attribution.
          if (isCaretakerProfileUnavailable(profileData)) {
            setUnavailable({ message: profileData.message });
            setCaretaker(null);
            return;
          }
        }

        setUnavailable(null);
        setCaretaker(profileData);
      } catch (err) {
        console.error('Error fetching caretaker profile:', err);
        setError('Failed to load caretaker profile. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchCaretakerProfile();
  }, [id, isOwner]);

  const handleBackToSearch = () => {
    navigate('/search-caretakers');
  };

  const handleSidebarOpen = (sidebarType: string) => {
    setActiveSidebar(sidebarType);
  };

  const handleSidebarClose = () => {
    setActiveSidebar(null);
    // Refresh profile data after editing
    if (isOwner) {
      fetchCaretakerProfile();
    }
  };

  const fetchCaretakerProfile = async () => {
    if (!id) return;
    
    try {
      let profileData;
      if (isOwner) {
        const caregiverProfile = await caregiverProfileService.getProfile();
        // Transform CaregiverProfile to CaretakerProfile interface
        profileData = {
          ...caregiverProfile,
          // Add missing fields that CaretakerProfile expects
          reviews: [],
          averageRating: 0,
          reviewCount: 0,
          ratingBreakdown: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
          // Add computed fields that CaretakerProfile expects
          displayName: `${caregiverProfile.firstName} ${caregiverProfile.lastName}`,
          experienceText: caregiverProfile.yearsExperience ? `${caregiverProfile.yearsExperience} years` : 'Not specified',
          hourlyRateText: caregiverProfile.hourlyRate ? `$${caregiverProfile.hourlyRate}/hour` : 'Not specified',
          languagesText: caregiverProfile.languages.length > 0 ? caregiverProfile.languages.join(', ') : 'Not specified',
        };
      } else {
        profileData = await caretakerProfileService.getCaretakerProfile(id);
      }
      setCaretaker(profileData);
    } catch (err) {
      console.error('Error refreshing profile:', err);
    }
  };

  const getCompletionPercentage = () => {
    if (!caretaker || !isOwner) return 0;
    
    const fields = [
      caretaker.bio,
      caretaker.phoneNumber,
      caretaker.location,
      caretaker.yearsExperience,
      caretaker.hourlyRate,
      caretaker.responseTime,
      caretaker.languages?.length > 0,
      caretaker.services?.length > 0,
      caretaker.availability?.length > 0,
      caretaker.certifications?.length > 0,
    ];
    
    const completedFields = fields.filter(Boolean).length;
    return Math.round((completedFields / fields.length) * 100);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (unavailable) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="info" sx={{ mb: 2 }} data-testid="not-a-fit-profile-empty">
          {unavailable.message}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={handleBackToSearch}
        >
          Back to Search
        </Button>
      </Container>
    );
  }

  if (error || !caretaker) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Caretaker not found'}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={handleBackToSearch}
        >
          Back to Search
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="body2"
          onClick={handleBackToSearch}
          sx={{ cursor: 'pointer' }}
        >
          Search Caretakers
        </Link>
        <Typography color="text.primary">
          {caretaker.displayName}
        </Typography>
      </Breadcrumbs>

      {/* Back Button */}
      <Button
        variant="outlined"
        startIcon={<ArrowBack />}
        onClick={handleBackToSearch}
        sx={{ mb: 3 }}
      >
        Back to Search
      </Button>

      {/* Profile Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ flexGrow: 1 }}>
          <CaretakerProfileHeader caretaker={caretaker} />
        </Box>
        {!isOwner && (
          <>
            {/* FEAT-057 — Save / Favorite Caregivers (MVP-18.29). Heart toggle
                on the caregiver profile; auth-gated via FavoriteButton. */}
            <FavoriteButton
              caregiverId={caretaker.id}
              summary={{
                id: caretaker.id,
                firstName: caretaker.firstName ?? null,
                lastName: caretaker.lastName ?? null,
                avatar: caretaker.avatar ?? null,
                bio: caretaker.bio ?? null,
                location: caretaker.location ?? null,
                hourlyRate: caretaker.hourlyRate ?? null,
                yearsExperience: caretaker.yearsExperience ?? null,
                languages: caretaker.languages ?? [],
              }}
            />
            <IconButton
              aria-label="More profile options"
              data-testid="profile-overflow-button"
              onClick={(event) => setOverflowAnchor(event.currentTarget)}
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={overflowAnchor}
              open={Boolean(overflowAnchor)}
              onClose={() => setOverflowAnchor(null)}
            >
              <NotAFitMenuItem
                targetUserId={caretaker.id}
                onCloseMenu={() => setOverflowAnchor(null)}
                onMarked={() => navigate('/search-caretakers')}
              />
              <MenuItem
                data-testid="report-profile-menu-item"
                aria-label="Report this profile"
                onClick={() => {
                  setReportDialogOpen(true);
                  setOverflowAnchor(null);
                }}
              >
                <ListItemIcon>
                  <OutlinedFlag fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Report this profile" />
              </MenuItem>
            </Menu>
            <ReportProfileDialog
              open={reportDialogOpen}
              onClose={() => setReportDialogOpen(false)}
              targetType="PROFILE"
              targetId={caretaker.id}
              context={{
                kind: 'profile',
                profileName: caretaker.displayName ?? null,
              }}
            />
          </>
        )}
      </Box>

      {/* FEAT-039 — Trust indicator badges row (MVP-8.1, 8.3, 8.37-8.40,
          18.14). Public-vs-authenticated filtering happens server-side. */}
      {caretaker?.id && (
        <Box sx={{ mt: 2 }} data-testid="profile-badge-row">
          <CaregiverBadgeRow caregiverId={caretaker.id} size="small" density="spacious" />
        </Box>
      )}

      {/* FEAT-047 — Careseeker-facing verify link (MVP-8.18, 8.35a, 8.35c).
          Renders nothing when the caregiver's state is unmapped OR when no
          licenseNumber is on file — never a broken or generic link. */}
      {!isOwner && caretaker?.id && (
        <StateAgencyVerifyLink
          stateCode={(caretaker as { stateCode?: string | null }).stateCode ?? null}
          licenseNumber={
            (caretaker as { stateLicenseNumber?: string | null }).stateLicenseNumber ?? null
          }
        />
      )}

      {/* FEAT-040 — Public Profile Completion badge (MVP-8.4). Replaces any
          employer-style "onboarded" label with the canonical % indicator. */}
      {!isOwner && caretaker?.id && (
        <Box sx={{ mt: 2 }}>
          <ProfileCompletionMeter mode="public" caregiverId={caretaker.id} />
        </Box>
      )}

      <Grid container spacing={3} sx={{ mt: 2 }}>
        {/* Left Column - Main Content */}
        <Grid item xs={12} lg={8}>
          {/* Profile Completion Card - Only for owners */}
          {isOwner && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Profile Completion
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {getCompletionPercentage()}% Complete
                    </Typography>
                  </Box>
                  <Box sx={{ width: '100%', bgcolor: 'grey.200', borderRadius: 1, height: 8 }}>
                    <Box
                      sx={{
                        width: `${getCompletionPercentage()}%`,
                        bgcolor: 'primary.main',
                        height: '100%',
                        borderRadius: 1,
                        transition: 'width 0.3s ease',
                      }}
                    />
                  </Box>
                </Box>
                {getCompletionPercentage() < 100 && (
                  <Typography variant="body2" color="text.secondary">
                    Complete your profile to attract more patients!
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}

          {/* FEAT-067 — Experience overview (MVP-18.5). */}
          {id && <ProfileSummary caregiverId={id} />}

          {/* Bio Section */}
          <CaretakerBio 
            caretaker={caretaker} 
            isOwner={isOwner}
            onEditBio={() => handleSidebarOpen('bio')}
          />

          {/* Services Section */}
          <CaretakerServices 
            services={caretaker.services} 
            isOwner={isOwner}
            onManageServices={() => handleSidebarOpen('services')}
          />

          {/* Availability Section */}
          <CaretakerAvailability 
            caretakerId={caretaker.id}
            availability={caretaker.availability} 
            isOwner={isOwner}
            onSetAvailability={() => handleSidebarOpen('availability')}
          />

          {/* Certifications Section */}
          <CaretakerCertifications 
            certifications={caretaker.certifications} 
            isOwner={isOwner}
            onManageCertifications={() => handleSidebarOpen('certifications')}
          />

          {/* Reviews Section */}
          <CaretakerReviews 
            reviews={caretaker.reviews || []}
            averageRating={caretaker.averageRating || 0}
            reviewCount={caretaker.reviewCount || 0}
            ratingBreakdown={caretaker.ratingBreakdown || {}}
          />
        </Grid>

        {/* Right Column - Sidebar */}
        <Grid item xs={12} lg={4}>
          <Box sx={{ position: 'sticky', top: 20 }}>
            {/* Contact Card - Only for non-owners */}
            {!isOwner && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Get in Touch
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Ready to start working with {caretaker.firstName}?
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <ContactCaretakerButton caretakerId={caretaker.id} />
                    <BookCaretakerButton 
                      caretakerId={caretaker.id}
                      caretakerName={caretaker.displayName}
                      caretakerAvatar={caretaker.avatar || undefined}
                    />
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Quick Info Card */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Quick Info
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Experience
                    </Typography>
                    <Typography variant="body1">
                      {caretaker.experienceText}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Hourly Rate
                    </Typography>
                    <Typography variant="body1">
                      {caretaker.hourlyRateText}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Languages
                    </Typography>
                    <Typography variant="body1">
                      {caretaker.languagesText}
                    </Typography>
                  </Box>
                  
                  {caretaker.responseTime && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Response Time
                      </Typography>
                      <Typography variant="body1">
                        {caretaker.responseTime}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Grid>
      </Grid>

      {/* Management Sidebars - Only for owners */}
      {isOwner && (
        <>
          <BioManagementSidebar
            open={activeSidebar === 'bio'}
            onClose={handleSidebarClose}
            currentBio={caretaker.bio || ''}
          />
          
          <ServicesManagementSidebar
            open={activeSidebar === 'services'}
            onClose={handleSidebarClose}
            services={caretaker.services || []}
          />
          
          <AvailabilityManagementSidebar
            open={activeSidebar === 'availability'}
            onClose={handleSidebarClose}
            availability={caretaker.availability || []}
          />
          
          <CertificationsManagementSidebar
            open={activeSidebar === 'certifications'}
            onClose={handleSidebarClose}
            certifications={caretaker.certifications || []}
          />
        </>
      )}
    </Container>
  );
};

export default CaretakerProfilePage;
